import { reportBelongsToSite, siteForReport } from '../../../lib/reportSiteIdentity';
/**
 * RerunConfigurationStep — merged Step 2 of the rerun panel.
 *
 * SRW (2026-05-19) — SSOT Rerun-Wizard Restoration. Wave 5-8 work
 * (RR5-1, RR5-2, IP2, IP3) had re-introduced inline editable controls
 * for the cross-cutting Site Project Intent fields (workloadProfile /
 * nuclearInterest / priority) inside this step. Per SSOT (row 0.99187 +
 * `Site_SSOT_Prompt.md` UX directive), those fields are canonical at the
 * site level and editing them inside the rerun wizard re-creates exactly
 * the "preferences differ from the site default" surface SSOT was
 * designed to remove. SRW-1 collapses the editable Site Project Intent
 * sub-section into a READ-ONLY summary card with a single "Edit at site
 * level →" link that deep-links to Overview → Site Project Intent (the
 * canonical edit affordance). The data path is unchanged — the rerun
 * wizard never edited a per-report copy of these fields; the legacy
 * inline editors wrote to the site doc anyway, just behind a misleading
 * "edit here" affordance.
 *
 * Rerun Step 2/Step 3 Consolidation (RR5-1). Replaces the prior split:
 *
 *   - Step 2 (legacy): `RerunProjectIntentStep` — workload + nuclear +
 *     priority + documents + version note + W4 derived-prefs mirror.
 *   - Step 3 (legacy): `ReportTypePreferencesFields` — typed prefs
 *     (projectSizeMW / customerCategory / inServiceDate / KPI weights).
 *
 * The two surfaces were visually identical (same heading style, same
 * dropdown styling) and stacked one above the other on the live walk,
 * which trained users to read them as "the same thing twice". RR5
 * collapses them into a single Configuration step with TWO clearly
 * labelled sub-sections + documents below:
 *
 *   - "Site project intent" — workload / nuclear / priority, shared
 *     across every report type on the site, persisted to
 *     `site.projectIntent`. SRW-1 (2026-05-19): rendered as a read-only
 *     summary panel with an "Edit at site level →" deep-link. Editable
 *     dropdowns are gone — they belong on the Site Configuration Panel.
 *   - "Report-type preferences" — type-specific knobs, persisted to
 *     `site.typedPreferencesDefaults[reportType]` or to
 *     `report.typedPreferencesOverride` depending on rerunConfigMode.
 *   - "Documents" — upload, library picker, exclude / priority toggles.
 *     Same surface as the legacy Step 2.
 *
 * Renamed from `RerunProjectIntentStep`. The site-intent sub-section
 * uses the same `FieldBand` banding the Report-type preferences use
 * (RR5-2) so the user reads ONE unified scoring & narrative surface.
 *
 * The version-note input is no longer mounted here — it lives on the
 * Confirm step (RR5-4) at the point of commit.
 */
import React from 'react';
import type { ProjectIntent, SiteDocument, SiteRagReport, TypedPreferencesByType } from '../../../types/siteRag';
import type { ReportTypeId } from '../../../config/reportTypes';
import type { SavedCustomBrief } from '../../../types/site';
import { Button } from '../../ui/button';
import { ChevronRight, Upload, FileText, FolderOpen, Trash2 } from 'lucide-react';
import { workloadProfileLabel } from '../WorkloadProfilePicker';
import { effectiveWorkloadProfile } from '../../../lib/workloadProfile';
import { useStore } from '../../../store';
import { toast } from 'sonner';
import type { SuggestedProjectIntent } from '../../../lib/suggestProjectIntent';
import { ReportTypePreferencesFields } from './ReportTypePreferencesFields';
import type { ReportTypePreferencesFieldsProps } from './ReportTypePreferencesFields';
import { missingIntentFields, missingIntentFieldsLabel } from '../../../lib/requiredIntentFields';

export interface RerunConfigurationStepProps {
  report: SiteRagReport;
  /** Report type — drives the embedded `ReportTypePreferencesFields`
   *  sub-section so the user edits the prefs for this report's type. */
  reportType: ReportTypeId;
  /** Currently-edited typed preferences for the report type. The
   *  embedded `ReportTypePreferencesFields` reads this as `value` and
   *  surfaces the type-specific banded form. */
  editedPrefs: TypedPreferencesByType[ReportTypeId];
  /** Baseline typed preferences (seeded on mount). Used by
   *  `ReportTypePreferencesFields` to render the "N changed" chip + the
   *  reset CTA against the mount-time seed. */
  initialEditedPrefs: TypedPreferencesByType[ReportTypeId];
  /** Setter for the typed-pref form (passed through to
   *  `ReportTypePreferencesFields` `onChange`). */
  setEditedPrefs: (next: NonNullable<TypedPreferencesByType[ReportTypeId]>) => void;
  /** Other report types currently active on this site — drives the CU5
   *  cross-type spillover hints inside the embedded prefs form. */
  availableOtherTypes?: ReportTypeId[];
  /** Forwarded to `ReportTypePreferencesFields` — drives CU6/CU7. */
  archetype?: ReportTypePreferencesFieldsProps<ReportTypeId>['archetype'];
  dam?: ReportTypePreferencesFieldsProps<ReportTypeId>['dam'];
  companyContext?: ReportTypePreferencesFieldsProps<ReportTypeId>['companyContext'];
  customBriefSite?: {
    id: string;
    savedCustomBriefs?: SavedCustomBrief[];
  };
  onSaveCustomBrief?: ReportTypePreferencesFieldsProps<ReportTypeId>['onSaveCustomBrief'];
  onDeleteCustomBrief?: ReportTypePreferencesFieldsProps<ReportTypeId>['onDeleteCustomBrief'];
  projectIntent: ProjectIntent;
  /**
   * SRW-1 (2026-05-19): kept on the prop surface for back-compat with
   * the parent panel (which still owns the state for the Review-step
   * delta diff), but the Configuration step no longer mutates intent
   * inline — the read-only summary card delegates edits to the Site
   * Configuration Panel via the `siteId` deep-link. Tests that asserted
   * `setProjectIntent` is invoked from this surface have been retired.
   */
  setProjectIntent: React.Dispatch<React.SetStateAction<ProjectIntent>>;
  /**
   * SRW-1 — site doc id used to build the "Edit at site level →" deep
   * link in the read-only Site Project Intent summary card. When
   * undefined (legacy reports with no `siteId`), the link is rendered
   * disabled — the SSOT contract still holds (no inline editing) but
   * the user can't deep-link to the site overview from here. */
  siteId?: string;
  uploadedDocs: SiteDocument[];
  setUploadedDocs: React.Dispatch<React.SetStateAction<SiteDocument[]>>;
  excludedDocIds: Set<string>;
  setExcludedDocIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  isProcessingDoc: boolean;
  docProgress: string;
  showLibraryPicker: boolean;
  setShowLibraryPicker: React.Dispatch<React.SetStateAction<boolean>>;
  /** Accepted-file filter — owned by the panel so the accept lists stay in
   *  one place. */
  filterValidFiles: (files: FileList | null) => File[];
  /** Batch upload handler — owned by the panel (uses safeImport + store). */
  onBatchUpload: (valid: File[], isFolder?: boolean) => Promise<void>;
  /**
   * Intent Prefill IP2 — context-aware suggestions computed by the parent
   * panel via `suggestProjectIntent`. Each field carries `{value, source,
   * rationale, confidence}`. The chip renders next to its dropdown when
   * the dropdown is on `'undecided'`; click accepts the suggestion and
   * stamps `projectIntent.fieldSource` for provenance. Optional — when
   * undefined no chips render (test mode / fresh wizard before suggester
   * is wired). */
  suggestion?: SuggestedProjectIntent;
  sectionColumnLayouts?: Record<string, 1 | 2 | 3>;
  setSectionColumnLayouts?: React.Dispatch<React.SetStateAction<Record<string, 1 | 2 | 3>>>;
  onBack: () => void;
  onNext: () => void;
}

/**
 * Intent Prefill IP2 — short labels for the dropdown enum values, used as
 * the chip caption when surfacing a suggestion. Kept inline (not pulled
 * from an option list) so the chip text matches the dropdown's
 * presentation verbatim.
 */
const NUCLEAR_LABEL: Record<'primary' | 'considering' | 'no', string> = {
  primary: 'Primary',
  considering: 'Considering',
  no: 'No',
};
const PRIORITY_LABEL: Record<'cost' | 'speed' | 'sustainability' | 'resilience', string> = {
  cost: 'Cost',
  speed: 'Speed',
  sustainability: 'Sustainability',
  resilience: 'Resilience',
};

export function RerunConfigurationStep(props: RerunConfigurationStepProps) {
  const {
    report, reportType, editedPrefs, initialEditedPrefs, setEditedPrefs,
    availableOtherTypes, archetype, dam, companyContext, customBriefSite, onSaveCustomBrief, onDeleteCustomBrief,
    projectIntent,
    siteId,
    uploadedDocs, setUploadedDocs, excludedDocIds, setExcludedDocIds,
    isProcessingDoc, docProgress, showLibraryPicker, setShowLibraryPicker,
    filterValidFiles, onBatchUpload,
    suggestion,
    onBack, onNext,
  } = props;

  // SRW-1 (2026-05-19) — IP2 per-field chips + IP3 Apply-all CTA + the
  // per-field acceptor helpers used to mount here on top of the editable
  // dropdowns. With the dropdowns retired in favour of a read-only
  // summary card, the in-step suggestion plumbing is gone — IP3
  // Apply-all already lives at site-creation time via
  // `IntentPrefillBanner` (wired into `NewReportWizard`), which is the
  // correct SSOT-compliant placement for prefill (the field set is
  // declared once, at site level). The `setProjectIntent` setter is
  // intentionally unread in this body — kept on the prop surface so the
  // parent panel still owns the canonical state for the Review-step
  // delta diff; we just don't write through to it from here anymore.

  // Stable heading ids for `aria-labelledby` — each sub-section announces
  // its own h3 to assistive tech (RR5-5 a11y sweep).
  const intentHeadingId = 'rerun-config-site-intent-heading';
  const prefsHeadingId = 'rerun-config-report-prefs-heading';

  // SRW-4 — site-link target for both the read-only summary card and the
  // missing-intent banner button. Mirrors the deep-link convention
  // already used by `rerun-edit-deep-link-intent` (single-page app
  // routes site overview via `/?site={id}&tab=overview&focus=intent`).
  const siteIntentHref = siteId
    ? `/?site=${encodeURIComponent(siteId)}&tab=overview&focus=intent#site-project-intent`
    : undefined;

  // SSOT-9 — non-blocking banner when intent lacks fields the active
  // report type depends on. Mounted at the top of the rerun config
  // step so the user sees it before scrolling into the form sections.
  const missingIntent = missingIntentFields(reportType, projectIntent);

  return (
    <div className="space-y-4">
      {missingIntent.length > 0 && (
        <div
          data-testid="ssot-missing-intent-banner-rerun"
          className="px-3 py-2 bg-warning/10 border border-warning/30 text-warning text-[10px] flex items-center justify-between gap-3 rounded"
        >
          <span>
            Site Project Intent is missing: <strong>{missingIntentFieldsLabel(missingIntent)}</strong>.
            These power the report's reasoning.
          </span>
          {/* SRW-4 — one-click escalation to the canonical edit surface.
                Renders an <a> rather than a router-aware button so the
                wizard doesn't need to ingest a navigate() handler (works
                identically in production + jsdom test mounts without
                BrowserRouter shimming). Falls back to a disabled <span>
                when siteId is unavailable (legacy reports). */}
          {siteIntentHref ? (
            <a
              href={siteIntentHref}
              data-testid="ssot-missing-intent-banner-rerun-cta"
              className="shrink-0 underline font-bold uppercase tracking-widest type-body hover:text-warning/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-warning focus-visible:ring-offset-1"
            >
              Open Site Project Intent →
            </a>
          ) : null}
        </div>
      )}

      {/* ═══ Sub-section A — Site project intent (READ-ONLY summary) ═══
            SRW-1 (2026-05-19): the editable dropdowns + workload picker
            + IP2 per-field chips + IP3 Apply-all CTA that mounted here
            via RR5-1 / RR5-2 / IP2 / IP3 are gone. SSOT says Site
            Project Intent is canonical at the site level; the rerun
            wizard surfaces it as a read-only mirror with a single
            "Edit at site level →" deep-link. The legacy inline editors
            wrote to the site doc anyway — the data path is preserved,
            we only removed the misleading "edit here" affordance. */}
      <section
        className="border border-border p-3 bg-state-hover/50 space-y-2 rounded-lg"
        aria-labelledby={intentHeadingId}
        data-testid="config-step-site-intent-summary"
      >
        <header className="flex items-center justify-between gap-2">
          <div>
            <h3
              id={intentHeadingId}
              className="type-body font-bold uppercase tracking-widest text-text-secondary"
            >
              Site project intent
            </h3>
            <p className="type-meta text-text-muted mt-0.5">
              Shared across every report on this site.
            </p>
          </div>
          {siteIntentHref ? (
            <a
              href={siteIntentHref}
              data-testid="config-step-site-intent-edit-link"
              className="type-body uppercase tracking-widest text-primary hover:text-primary/80 underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
            >
              Edit at site level →
            </a>
          ) : (
            <span
              data-testid="config-step-site-intent-edit-link-disabled"
              className="type-body uppercase tracking-widest text-text-muted"
              title="Site id unavailable on this report — edit Site Project Intent from the site landing page."
            >
              Edit at site level
            </span>
          )}
        </header>
        <dl className="grid grid-cols-3 gap-3 type-body">
          <div>
            <dt className="text-text-muted uppercase tracking-widest">Workload</dt>
            <dd
              className="font-mono text-text-primary mt-0.5"
              data-testid="config-step-site-intent-summary-workload"
            >
              {workloadProfileLabel(effectiveWorkloadProfile(projectIntent))}
            </dd>
          </div>
          <div>
            <dt className="text-text-muted uppercase tracking-widest">Nuclear</dt>
            <dd
              className="font-mono text-text-primary mt-0.5"
              data-testid="config-step-site-intent-summary-nuclear"
            >
              {projectIntent.nuclearInterest === 'undecided'
                ? '—'
                : NUCLEAR_LABEL[projectIntent.nuclearInterest as Exclude<ProjectIntent['nuclearInterest'], 'undecided'>]}
            </dd>
          </div>
          <div>
            <dt className="text-text-muted uppercase tracking-widest">Priority</dt>
            <dd
              className="font-mono text-text-primary mt-0.5"
              data-testid="config-step-site-intent-summary-priority"
            >
              {projectIntent.priority === 'undecided'
                ? '—'
                : PRIORITY_LABEL[projectIntent.priority as Exclude<ProjectIntent['priority'], 'undecided'>]}
            </dd>
          </div>
        </dl>
      </section>

      {/* ═══ Sub-section B — Report-type preferences ═══
            RR5-1: the typed-preference form that lived as a sibling
            column / separate step is now an inline sub-section of the
            merged Configuration step. Same `ReportTypePreferencesFields`
            component renders here as before — the only thing that
            changed is where it mounts. */}
      <section
        aria-labelledby={prefsHeadingId}
        data-testid="config-step-report-prefs-section"
      >
        <header className="mb-2">
          <h3
            id={prefsHeadingId}
            className="type-body font-bold uppercase tracking-widest text-text-secondary"
          >
            Report-type preferences
          </h3>
          <p className="type-meta text-text-muted mt-0.5">
            Applies to this report only.
          </p>
        </header>
        <ReportTypePreferencesFields
          reportType={reportType}
          value={editedPrefs as NonNullable<TypedPreferencesByType[ReportTypeId]>}
          baseline={initialEditedPrefs as NonNullable<TypedPreferencesByType[ReportTypeId]>}
          onChange={setEditedPrefs}
          availableOtherTypes={availableOtherTypes}
          archetype={archetype}
          dam={dam}
          companyContext={companyContext}
          customBriefSite={customBriefSite}
          onSaveCustomBrief={onSaveCustomBrief}
          onDeleteCustomBrief={onDeleteCustomBrief}
        />
      </section>

      {/* Sub-section B.2 — Section Layout Overrides — REMOVED (William 2026-07-04):
          the per-section 1-col/2-col override is redundant in the Rerun flow now
          that column layout is owned by Template Studio (Section Layouts there).
          The `sectionColumnLayouts` prop remains optional on the interface for
          back-compat with callers; it is no longer surfaced in the rerun config UI. */}

      {/* ═══ Sub-section C — Documents ═══
            Same surface as the legacy Step 2 docs picker; relocated
            below the configuration sub-sections so the user sees the
            scoring/narrative controls first and the document context
            second. */}
      <section
        className="border border-border p-3 space-y-2 rounded-lg"
        data-testid="config-step-documents-section"
      >
        <div className="flex items-center justify-between">
          <label className="type-body font-bold uppercase tracking-widest text-text-secondary">
            Report Documents ({uploadedDocs.length - excludedDocIds.size} / {uploadedDocs.length})
          </label>
        </div>
        <div className="flex gap-2">
          <label className="flex-1 block">
            <input type="file" multiple accept=".pdf,.docx,.doc,.txt,.csv,.xlsx,.xls,.md,.json,.rtf,.pptx,.ppt,.jpg,.jpeg,.png,.gif,.kmz,.kml,.eml,.msg,.url,.log,.xml,.html" className="hidden"
              onChange={async (e) => { await onBatchUpload(filterValidFiles(e.target.files)); e.target.value = ''; }} />
            <div className="border border-dashed border-border p-2 text-center cursor-pointer hover:bg-state-hover type-meta uppercase tracking-widest text-text-muted flex items-center justify-center gap-1 rounded-md">
              <Upload className="w-3 h-3" /> {isProcessingDoc ? `Processing (${docProgress})...` : 'Upload Files'}
            </div>
          </label>
          <label className="block">
            <input type="file" className="hidden" {...{ webkitdirectory: '', directory: '' } as Record<string, string>}
              onChange={async (e) => { await onBatchUpload(filterValidFiles(e.target.files), true); e.target.value = ''; }} />
            <div className="border border-dashed border-border p-2 text-center cursor-pointer hover:bg-state-hover type-meta uppercase tracking-widest text-text-muted flex items-center justify-center gap-1 px-3 rounded-md">
              <FolderOpen className="w-3 h-3" /> Folder
            </div>
          </label>
          <button
            onClick={() => setShowLibraryPicker(true)}
            className="border border-dashed border-border p-2 text-center cursor-pointer hover:bg-state-hover type-meta uppercase tracking-widest text-text-muted flex items-center justify-center gap-1 px-3 rounded-md">
            <FileText className="w-3 h-3" /> Add from Library
          </button>
        </div>
        {/* Library picker */}
        {showLibraryPicker && (() => {
          // Get all docs from site library that aren't already in report docs
          const siteDocs = (() => {
            const site = siteForReport(useStore.getState().siteRag.sites, report);
            if (!site) return [];
            const allReports = useStore.getState().siteRag.reports.filter(r =>
              reportBelongsToSite(site, r)
            );
            const seen = new Set(uploadedDocs.map(d => d.id));
            const docs: SiteDocument[] = [];
            for (const r of allReports) {
              for (const d of r.documents || []) {
                if (!seen.has(d.id)) { seen.add(d.id); docs.push(d); }
              }
            }
            return docs;
          })();
          return (
            <div className="border border-border bg-surface-1 p-2 space-y-1 rounded-md overflow-hidden">
              <div className="flex items-center justify-between">
                <span className="type-meta font-bold uppercase tracking-widest text-text-secondary">Add from Document Library ({siteDocs.length} available)</span>
                <button onClick={() => setShowLibraryPicker(false)} className="type-meta text-text-secondary hover:text-text-primary">Close</button>
              </div>
              {siteDocs.length === 0 ? (
                <div className="type-meta text-text-muted">All library documents are already included</div>
              ) : (
                <div className="max-h-32 overflow-y-auto space-y-0.5">
                  {siteDocs.map(d => (
                    <button key={d.id} className="w-full text-left type-meta text-text-muted hover:bg-state-hover px-2 py-1 flex items-center gap-2"
                      onClick={() => {
                        setUploadedDocs(prev => [...prev, d]);
                        toast.success(`Added "${d.fileName}" to report documents`);
                      }}>
                      <FileText className="w-3 h-3 text-text-muted shrink-0" />
                      <span className="truncate flex-1">{d.fileName}</span>
                      <span className="text-text-secondary shrink-0">{(d.sizeBytes / 1024).toFixed(0)}KB</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })()}
        {uploadedDocs.length > 0 && (
          <div className="max-h-36 overflow-y-auto space-y-0.5">
            <div className="flex items-center justify-between type-meta text-text-secondary uppercase tracking-widest pb-1">
              <span>{uploadedDocs.length - excludedDocIds.size} included · {uploadedDocs.filter(d => d.priority).length} priority</span>
              <button className="hover:text-primary" onClick={() => setExcludedDocIds(new Set())}>Include All</button>
            </div>
            {uploadedDocs.map((d, i) => {
              const excluded = excludedDocIds.has(d.id);
              return (
              <div key={d.id} className={`flex items-center gap-1.5 type-meta group ${excluded ? 'text-text-secondary' : d.priority ? 'text-warning bg-warning/15/50' : 'text-text-muted'}`}>
                <input type="checkbox" checked={!excluded} className="w-3 h-3 shrink-0 accent-primary"
                  onChange={() => setExcludedDocIds(prev => { const n = new Set(prev); if (n.has(d.id)) n.delete(d.id); else n.add(d.id); return n; })} />
                <button title={d.priority ? 'Priority document (deep analysis)' : 'Mark as priority for deeper analysis'}
                  className={`shrink-0 transition-colors ${d.priority ? 'text-warning' : 'text-text-primary hover:text-warning'}`}
                  onClick={() => setUploadedDocs(prev => prev.map((doc, j) => j === i ? { ...doc, priority: !doc.priority } : doc))}>
                  <span className="text-sm">{d.priority ? '★' : '☆'}</span>
                </button>
                <FileText className={`w-3 h-3 shrink-0 ${excluded ? 'text-text-primary' : d.priority ? 'text-warning' : 'text-text-secondary'}`} />
                <span className={`truncate flex-1 ${excluded ? 'line-through' : ''}`}>{d.fileName}</span>
                {d.priority && <span className="text-[6px] font-bold uppercase tracking-widest text-warning shrink-0 px-1 bg-warning/15">NB</span>}
                <span className="text-text-secondary shrink-0">{(d.sizeBytes/1024).toFixed(0)}KB</span>
                <button className="opacity-0 group-hover:opacity-100 text-danger hover:text-danger shrink-0"
                  onClick={() => setUploadedDocs(prev => prev.filter((_, j) => j !== i))}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              );
            })}
          </div>
        )}
      </section>

      {/* RR5-4 — version note + "Why are you rerunning?" input lives on
           the Confirm step now (above the Run CTA). The input is a
           commit-message presentationally, not a configuration knob, so
           it belongs at the point of commit. */}

      {/* Next button */}
      <div className="flex justify-between pt-2">
        <Button variant="outline" size="sm" className="h-8 type-body uppercase tracking-widest" onClick={onBack}>
          Back
        </Button>
        <Button size="sm" className="h-9 type-meta font-bold" onClick={onNext}>
          Review &amp; Run <ChevronRight className="w-3.5 h-3.5 ml-1" />
        </Button>
      </div>
    </div>
  );
}
